import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { BulkDeleteMaterialsDto } from '@models/bulk-delete-materials-dto.interface';
import { MaterialListRowDto } from '@models/material-list-row-dto.interface';
import { MaterialsService } from '../../../../services/materials.service';

function dateRangeValidator(group: AbstractControl): ValidationErrors | null {
  const from = group.get('from')?.value as string | null;
  const to = group.get('to')?.value as string | null;
  if (from && to && to < from) {
    return { dateRangeInvalid: true };
  }
  return null;
}

@Component({
  selector: 'app-bulk-delete-materials-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatCheckboxModule,
  ],
  templateUrl: './bulk-delete-materials-dialog.component.html',
  styleUrl: './bulk-delete-materials-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkDeleteMaterialsDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<BulkDeleteMaterialsDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly materialsService = inject(MaterialsService);

  protected readonly currentStep = signal<1 | 2>(1);
  protected readonly previewItems = signal<MaterialListRowDto[]>([]);
  protected readonly isLoadingPreview = signal(false);
  protected readonly isDeleting = signal(false);
  protected readonly selectedIds = signal<Set<number>>(new Set<number>());

  protected readonly displayedColumns: string[] = [
    'select',
    'title',
    'barcode',
    'receivedOn',
    'wellTitle',
    'rigAlias',
  ];

  protected form: FormGroup = this.fb.group(
    {
      from: [null, Validators.required],
      to: [null, Validators.required],
    },
    { validators: dateRangeValidator }
  );

  protected get fromControl() {
    return this.form.get('from')!;
  }

  protected get toControl() {
    return this.form.get('to')!;
  }

  protected get dateRangeInvalid(): boolean {
    return this.form.hasError('dateRangeInvalid') && this.toControl.touched;
  }

  protected onCancel(): void {
    this.dialogRef.close(undefined);
  }

  protected onBack(): void {
    this.currentStep.set(1);
    this.previewItems.set([]);
    this.selectedIds.set(new Set<number>());
    this.dialogRef.updateSize('680px');
  }

  protected onNext(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const dto: BulkDeleteMaterialsDto = {
      from: this.form.value.from as string,
      to: this.form.value.to as string,
    };

    this.isLoadingPreview.set(true);
    this.materialsService.previewBulkDelete(dto).subscribe({
      next: (items) => {
        this.previewItems.set(items);
        this.selectedIds.set(new Set<number>(items.map((item) => item.id)));
        this.isLoadingPreview.set(false);
        this.currentStep.set(2);
        this.dialogRef.updateSize('1000px');
      },
      error: () => {
        this.isLoadingPreview.set(false);
      },
    });
  }

  protected isSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  protected toggleSelection(id: number, checked: boolean): void {
    const updated = new Set(this.selectedIds());
    if (checked) {
      updated.add(id);
    } else {
      updated.delete(id);
    }
    this.selectedIds.set(updated);
  }

  protected get allSelected(): boolean {
    const items = this.previewItems();
    return items.length > 0 && this.selectedIds().size === items.length;
  }

  protected get someSelected(): boolean {
    const size = this.selectedIds().size;
    return size > 0 && size < this.previewItems().length;
  }

  protected toggleSelectAll(event: MatCheckboxChange): void {
    this.selectedIds.set(event.checked ? new Set(this.previewItems().map((item) => item.id)) : new Set<number>());
  }

  protected onConfirm(): void {
    const dto: BulkDeleteMaterialsDto = {
      from: this.form.value.from as string,
      to: this.form.value.to as string,
      materialIds: Array.from(this.selectedIds()),
    };
    this.dialogRef.close(dto);
  }
}
